import React, { useEffect, useState } from "react";
import { Copy, Check, Users, Plus, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function FamilyInviteBlock() {
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadMembers = async () => {
    try {
      const res = await api.get("/families/members");
      setMembers(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadMembers(); }, []);

  const createInvite = async () => {
    setBusy(true);
    try {
      const res = await api.post("/families/invite");
      setInviteCode(res.data?.code || "");
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/login?join=${inviteCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="mt-20 p-6 sm:p-8 bg-white hairline-cream" style={{ borderRadius: 4 }} data-testid="family-invite-block">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="label-ui text-[11px] text-[#B8973A] mb-2 tracking-[0.25em]">FAMILY MEMBERS</p>
          <h3 className="text-2xl" style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33" }}>
            Invite the ones who&apos;ll ask the questions.
          </h3>
          <p className="text-[#8C8278] italic mt-2" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
            Grandchildren, sons, daughters, siblings — anyone who wants to leave hearts, voice notes, or send a request.
          </p>
        </div>
        <Users size={22} className="text-[#B8973A] shrink-0 mt-1" />
      </div>

      {members.length > 0 && (
        <div className="mt-5 mb-6 space-y-2" data-testid="family-members-list">
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid #F0EAE0" }}>
              <div>
                <p className="text-[#111D33]" style={{ fontFamily: "Georgia, serif" }}>{m.name || m.email}</p>
                <p className="label-ui text-[10px] text-[#8C8278] mt-0.5">
                  {m.is_owner ? "OWNER" : "MEMBER"} · {m.email}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!inviteCode ? (
        <button
          onClick={createInvite}
          disabled={busy}
          className="btn-outline-navy flex items-center gap-2 mt-2"
          data-testid="create-invite-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create an invite link
        </button>
      ) : (
        <div className="mt-5 p-5 bg-[#F7F0E0]" style={{ border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="invite-created-panel">
          <p className="label-ui text-[10px] text-[#B8973A] mb-2 tracking-[0.25em]">INVITE CODE</p>
          <div className="flex items-center gap-3 mb-4">
            <span
              className="text-2xl tracking-[0.3em]"
              style={{ fontFamily: "Cormorant Garamond, serif", color: "#111D33" }}
              data-testid="invite-code-value"
            >
              {inviteCode}
            </span>
          </div>
          <p className="text-sm text-[#8C8278] mb-4" style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}>
            Share the code, or copy the sign-up link and send it to them. When they log in and enter this code, they&apos;ll be able to see your stories and leave reactions.
          </p>
          <button
            onClick={copyLink}
            className="btn-primary flex items-center gap-2"
            data-testid="copy-invite-link-btn"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy invite link"}
          </button>
        </div>
      )}
    </div>
  );
}
